import React from 'react';
import classNames from 'classnames';
import {reach} from 'hoek';
import ArticleImage from 'presentational/ArticleImage/ArticleImage';
import CardCarousel from 'presentational/CardCarousel/CardCarousel';
import {createHashFromUnderline} from 'utils/utils';
import {BLOCKS, INLINES, MARKS} from '@contentful/rich-text-types';
import './RichText.less';

function isText(node) {
    return node.nodeType === 'text';
}

function defaultInline(type, node) {
    return `<span>type: ${type} id: ${node.data.target.sys.id}</span>`;
}

function headingProps(node) {
    const id = createHashFromUnderline(node);
    return {
        id: id && id.hash,
        className: classNames({RichText__anchor: !!id}),
    };
}

/* istanbul ignore next */
const defaultNodeRenderers = {
    [BLOCKS.HEADING_2]: (node, next) => {
        return <h2 {...headingProps(node)}>{next(node.content)}</h2>;
    },
    [BLOCKS.HEADING_3]: (node, next) => {
        return <h3 {...headingProps(node)}>{next(node.content)}</h3>;
    },
    [BLOCKS.HEADING_4]: (node, next) => <h4>{next(node.content)}</h4>,
    [BLOCKS.HEADING_5]: (node, next) => <h5>{next(node.content)}</h5>,
    [BLOCKS.HEADING_6]: (node, next) => <h6>{next(node.content)}</h6>,
    [BLOCKS.PARAGRAPH]: (node, next) => <p>{next(node.content)}</p>,
    [BLOCKS.OL_LIST]: (node, next) => <ol>{next(node.content)}</ol>,
    [BLOCKS.UL_LIST]: (node, next) => <ul>{next(node.content)}</ul>,
    [BLOCKS.LIST_ITEM]: (node, next) => <li>{next(node.content)}</li>,
    [BLOCKS.HR]: () => <hr />,
    [BLOCKS.QUOTE]: (node, next) => (
        <blockquote>{next(node.content)}</blockquote>
    ),
    [INLINES.ASSET_HYPERLINK]: (node) =>
        defaultInline(INLINES.ASSET_HYPERLINK, node),
    [INLINES.ENTRY_HYPERLINK]: (node) =>
        defaultInline(INLINES.ENTRY_HYPERLINK, node),
    [INLINES.EMBEDDED_ENTRY]: (node) =>
        defaultInline(INLINES.EMBEDDED_ENTRY, node),
    [INLINES.HYPERLINK]: (node, next) => (
        <a href={node.data.uri} className="text-link">
            {next(node.content)}
        </a>
    ),
};

/* istanbul ignore next */
const defaultMarkRenderers = {
    [MARKS.BOLD]: (text) => <strong>{text}</strong>,
    [MARKS.ITALIC]: (text) => <em>{text}</em>,
    [MARKS.UNDERLINE]: (text) => <span className="rt-ul">{text}</span>,
    [MARKS.CODE]: (text) => <pre>{text}</pre>,
};

/* istanbul ignore next */
function nodeToHtmlString(node, key) {
    if (isText(node)) {
        if (node.marks.length > 0) {
            return node.marks.reduce((value, mark) => {
                if (!defaultMarkRenderers[mark.type]) {
                    return value;
                }
                return {
                    ...defaultMarkRenderers[mark.type](value),
                    key,
                };
            }, node.value);
        }
        return node.value;
    }
    const nextNode = function(nodes) {
        return nodes.map((n, idx) =>
            nodeToHtmlString(n, `block-entry-child-${idx}`),
        );
    };
    if (!node.nodeType || !defaultNodeRenderers[node.nodeType]) {
        return null;
    }

    return {
        ...defaultNodeRenderers[node.nodeType](node, nextNode),
        key,
    };
}

export default ({richTextContent, isDevice, translations}) => {
    const content = reach(richTextContent, 'json.content');
    const blocks = reach(richTextContent, 'links.entries.block').reduce(
        (data, block) => {
            if (block) {
                data[block.sys.id] = block;
            }
            return data;
        },
        {},
    );

    return (
        <div className="article-content-container" itemProp="articleBody">
            {content.map((node) => {
                const key = `block-entry-${content.indexOf(node)}`;
                if (node.nodeType === BLOCKS.EMBEDDED_ENTRY) {
                    const block = blocks[node.data.target.sys.id];
                    if (block && block.externalLinksCollection) {
                        return (
                            <CardCarousel
                                {...{isDevice, translations, key}}
                                title={block.title}
                                cards={reach(
                                    block,
                                    'externalLinksCollection.items',
                                )}
                                isInArticle
                            />
                        );
                    }
                    return <ArticleImage {...block} key={key} />;
                }
                return nodeToHtmlString(node, key);
            })}
        </div>
    );
};
